import { useEffect, useRef, useState } from 'react';
import { strapiService, type ArticlesListItem } from '@/services/StrapiService';
import { useSsrPrimed } from '@/ssr/ssrData';
import { ssrDataKeys } from '@/ssr/ssrTypes';

export interface UseArticlesListResult {
    items: ArticlesListItem[];
    isLoading: boolean;
    error: string | null;
}

/**
 * Loads the full ordered article summary list. `listArticles()` returns the whole set in one
 * immutable-cached, summary-only artifact with no article bodies so callers slice it client-side
 * rather than making a request per page.
 */
export const useArticlesList = (): UseArticlesListResult => {
    // The list the server already rendered, when this document was server-rendered.
    const primed = useSsrPrimed<ArticlesListItem[]>(ssrDataKeys.articleList());
    const primedRef = useRef(primed !== undefined);
    const [items, setItems] = useState<ArticlesListItem[]>(primed ?? []);
    const [isLoading, setIsLoading] = useState(primed === undefined);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (primedRef.current) {
            primedRef.current = false;
            return;
        }
        let cancelled = false;
        setIsLoading(true);
        setError(null);
        strapiService
            .listArticles()
            .then((list) => {
                if (!cancelled) {
                    setItems(list);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Failed to load resources');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return { items, isLoading, error };
};
